/**
 * Become School Form Component
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { ImageUpload, IMAGE_FORMATS } from '../ui/ImageUpload';
import { GoogleRatingInput } from '../GoogleRatingInput';
import { ChevronRight, Info } from 'lucide-react';
import axios from 'axios';
import { AVAILABLE_COUNTRIES } from '../../data/postalCodes';
import { API_URL, SOCIAL_PLATFORMS, initialSocialLinks, isSpainCountry, lookupPostalCodeWithGeo } from './formUtils';

export function BecomeSchoolForm() {
  const { t, i18n } = useTranslation();
  const { getAuthHeaders } = useAuthStore();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  
  const [logoImage, setLogoImage] = useState(null);
  const [nameEn, setNameEn] = useState('');
  const [nameEs, setNameEs] = useState('');
  const [nameIt, setNameIt] = useState('');
  const [descEn, setDescEn] = useState('');
  const [descEs, setDescEs] = useState('');
  const [descIt, setDescIt] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [country, setCountry] = useState('España');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [province, setProvince] = useState('');
  const [area, setArea] = useState('');
  const [cityAutoFilled, setCityAutoFilled] = useState(false);
  const [cityOptions, setCityOptions] = useState([]);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  
  // Google Business Rating
  const [googleMapsUrl, setGoogleMapsUrl] = useState('');
  const [googleRating, setGoogleRating] = useState(null);
  const [googleReviewsCount, setGoogleReviewsCount] = useState(null);
  
  // Social links state
  const [socialLinks, setSocialLinks] = useState(initialSocialLinks);
  
  const updateSocialLink = (platform, value) => {
    setSocialLinks(prev => ({ ...prev, [platform]: value }));
  };

  // Auto-fill city, province, area when postal code changes
  useEffect(() => {
    const lookupAddress = async () => {
      if (postalCode.length >= 4) {
        try {
          const result = await lookupPostalCodeWithGeo(postalCode, country);
          if (result.found) {
            if (result.hasMultipleCities) {
              setCityOptions(result.cities || []);
              setCity('');
              setCityAutoFilled(false);
            } else {
              setCity(result.city || '');
              setProvince(result.province || '');
              setArea(result.area || '');
              setCityAutoFilled(true);
              setCityOptions([]);
            }
          }
        } catch (error) {
          console.log('Postal code lookup error:', error);
        }
      }
    };
    
    lookupAddress();
  }, [postalCode, country]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!nameEs.trim() || !descEs.trim() || !city.trim() || !country.trim() || !phone.trim() || !postalCode.trim()) {
      alert(t('school.becomeSchool.requiredFieldsError'));
      return;
    }
    
    if (!acceptedTerms) {
      alert(t('school.becomeSchool.termsRequiredError'));
      return;
    }
    
    setIsLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/schools/register`,
        {
          name: { en: nameEn || nameEs, es: nameEs, it: nameIt || nameEs },
          description: { en: descEn || descEs, es: descEs, it: descIt || descEs },
          phone,
          website: website || null,
          images: logoImage ? [logoImage] : [],
          location: {
            address: '',
            city,
            province,
            area,
            country,
            postal_code: postalCode,
            latitude: 0,
            longitude: 0,
          },
          social_links: {
            instagram: socialLinks.instagram || null,
            facebook: socialLinks.facebook || null,
            youtube: socialLinks.youtube || null,
            tiktok: socialLinks.tiktok || null,
            linkedin: socialLinks.linkedin || null,
            twitter: socialLinks.twitter || null,
            telegram: socialLinks.telegram || null,
          },
          // Google Business Rating
          google_maps_url: googleMapsUrl || null,
          google_rating: googleRating,
          google_reviews_count: googleReviewsCount,
        },
        { headers: getAuthHeaders() }
      );
      
      alert(t('school.becomeSchool.successMessage'));
      navigate('/profile');
    } catch (error) {
      alert(error.response?.data?.detail || t('school.becomeSchool.errorSubmit'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 animate-fade-in" data-testid="become-school-page">
      <button onClick={() => navigate(-1)} className="flex items-center text-secondary mb-6 hover:text-gold">
        <ChevronRight className="h-4 w-4 rotate-180 mr-1" /> {t('common.back')}
      </button>
      
      <h1 className="font-serif text-3xl font-bold text-primary mb-6">{t('profile.becomeSchool')}</h1>
      
      <div className="flex gap-3 p-4 rounded-xl mb-6" style={{ backgroundColor: '#F5EFE6' }}>
        <Info className="w-6 h-6 text-gold flex-shrink-0" style={{ color: '#D4A574' }} />
        <p className="text-sm text-secondary">
          {t('school.becomeSchool.infoText')}
        </p>
      </div>
      
      <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm p-6 space-y-6">
        {/* School Logo */}
        <ImageUpload
          image={logoImage}
          onImageChange={setLogoImage}
          label={t('school.becomeSchool.schoolLogo')}
          aspectRatio={IMAGE_FORMATS.PROFILE.aspectRatio}
          maxSize={IMAGE_FORMATS.PROFILE.maxSize}
          shape="circle"
          placeholder={t('school.becomeSchool.addLogo')}
          folder="schools/"
          language={i18n.language}
        />
        
        {/* Name Fields */}
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">{t('school.becomeSchool.nameSpanish')} *</label>
          <Input placeholder={t('school.becomeSchool.nameSpanishPlaceholder')} value={nameEs} onChange={(e) => setNameEs(e.target.value)} required data-testid="school-name-es" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">{t('school.becomeSchool.nameEnglish')}</label>
          <Input placeholder={t('school.becomeSchool.nameEnglishPlaceholder')} value={nameEn} onChange={(e) => setNameEn(e.target.value)} data-testid="school-name-en" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">{t('school.becomeSchool.nameItalian')}</label>
          <Input placeholder={t('school.becomeSchool.nameItalianPlaceholder')} value={nameIt} onChange={(e) => setNameIt(e.target.value)} data-testid="school-name-it" />
        </div>
        
        {/* Description Fields */}
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">{t('school.becomeSchool.descSpanish')} *</label>
          <Textarea placeholder={t('school.becomeSchool.descSpanishPlaceholder')} value={descEs} onChange={(e) => setDescEs(e.target.value)} rows={4} required data-testid="school-desc-es" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">{t('school.becomeSchool.descEnglish')}</label>
          <Textarea placeholder={t('school.becomeSchool.descEnglishPlaceholder')} value={descEn} onChange={(e) => setDescEn(e.target.value)} rows={4} data-testid="school-desc-en" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">{t('school.becomeSchool.descItalian')}</label>
          <Textarea placeholder={t('school.becomeSchool.descItalianPlaceholder')} value={descIt} onChange={(e) => setDescIt(e.target.value)} rows={4} data-testid="school-desc-it" />
        </div>
        
        {/* Contact */}
        <h3 className="font-semibold text-primary pt-4 border-t">{t('school.becomeSchool.contactInfo')}</h3>
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">{t('school.becomeSchool.phoneLabel')} *</label>
          <Input placeholder={t('school.becomeSchool.phonePlaceholder')} value={phone} onChange={(e) => setPhone(e.target.value)} required data-testid="school-phone" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">{t('school.becomeSchool.websiteLabel')}</label>
          <Input placeholder={t('school.becomeSchool.websitePlaceholder')} value={website} onChange={(e) => setWebsite(e.target.value)} type="url" data-testid="school-website" />
        </div>
        
        {/* Social Links */}
        <h3 className="font-semibold text-primary pt-4 border-t">{t('school.becomeSchool.socialLinks')}</h3>
        <p className="text-sm text-secondary mb-4">{t('school.becomeSchool.socialLinksDescription')}</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {SOCIAL_PLATFORMS.map(platform => (
            <div key={platform.key}>
              <label className="block text-sm font-medium text-secondary mb-1">{platform.label}</label>
              <Input
                placeholder={platform.placeholder.replace('tu_perfil', 'tu_escuela')}
                value={socialLinks[platform.key]}
                onChange={(e) => updateSocialLink(platform.key, e.target.value)}
                type="url"
              />
            </div>
          ))}
        </div>
        
        {/* Google Business Rating */}
        <GoogleRatingInput
          googleMapsUrl={googleMapsUrl}
          setGoogleMapsUrl={setGoogleMapsUrl}
          googleRating={googleRating}
          setGoogleRating={setGoogleRating}
          googleReviewsCount={googleReviewsCount}
          setGoogleReviewsCount={setGoogleReviewsCount}
          showVerificationStatus={false}
        />
        
        {/* Location */}
        <h3 className="font-semibold text-primary pt-4 border-t">{t('school.becomeSchool.locationTitle')}</h3>
        <div>
          <label className="block text-sm font-semibold text-primary mb-2">{t('school.becomeSchool.countryLabel')} *</label>
          <Input placeholder="España" value={country} onChange={(e) => setCountry(e.target.value)} required data-testid="school-country" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-primary mb-2">{t('school.becomeSchool.postalCodeLabel')} *</label>
            <Input placeholder="Ej: 28001" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} maxLength={10} required data-testid="school-postal-code" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-primary mb-2">
              {t('school.becomeSchool.cityLabel')} * 
              {cityAutoFilled && <span className="text-green-600 text-xs ml-1">{t('school.becomeSchool.autoFilled')}</span>}
            </label>
            {cityOptions.length > 0 ? (
              <select
                value={city}
                onChange={(e) => {
                  setCity(e.target.value);
                  setCityAutoFilled(true);
                  setCityOptions([]);
                }}
                required
                className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-gold bg-white"
                data-testid="school-city-select"
              >
                <option value="">{t('common.selectCity', 'Selecciona una ciudad')}</option>
                {cityOptions.map((opt, idx) => (
                  <option key={idx} value={opt.city}>{opt.city}</option>
                ))}
              </select>
            ) : (
              <Input 
                placeholder={t('school.becomeSchool.cityAutoPlaceholder') || 'Compilato automaticamente dal CAP'} 
                value={city} 
                readOnly 
                required 
                className="bg-gray-100 cursor-not-allowed border-gray-200" 
                data-testid="school-city" 
              />
            )}
          </div>
        </div>
        
        {/* Terms and Conditions Checkbox */}
        <div className="flex items-start gap-3 p-4 rounded-xl border border-gray-200 bg-gray-50">
          <input
            type="checkbox"
            id="accept-terms-school"
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-gray-300 text-gold focus:ring-gold"
            style={{ accentColor: '#D4A574' }}
            data-testid="accept-terms-checkbox-school"
          />
          <label htmlFor="accept-terms-school" className="text-sm text-secondary cursor-pointer">
            {t('school.becomeSchool.termsAccept')}{' '}
            <Link to="/terms" target="_blank" className="text-gold hover:underline font-medium" style={{ color: '#D4A574' }}>
              {t('school.becomeSchool.termsLink')}
            </Link>
            {' '}{t('school.becomeSchool.andThe')}{' '}
            <Link to="/professional-guidelines" target="_blank" className="text-gold hover:underline font-medium" style={{ color: '#D4A574' }}>
              {t('school.becomeSchool.guidelinesLink')}
            </Link>.
          </label>
        </div>
        
        <Button type="submit" disabled={isLoading} className="w-full bg-gold text-white py-3" style={{ backgroundColor: '#D4A574' }} data-testid="submit-school">
          {isLoading ? t('school.becomeSchool.submitting') : t('school.becomeSchool.submitButton')}
        </Button>
      </form>
    </div>
  );
}
